import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Check, Trash2, AtSign, SmilePlus } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function initials(name, email) {
  const s = (name || email || "?").trim();
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function Avatar({ name, email, size = 32 }) {
  const palette = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];
  const seed = (name || email || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = palette[seed % palette.length];
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.4 }}
    >
      {initials(name, email)}
    </div>
  );
}

function highlightMentions(body, currentUserNameOrEmail) {
  const me = (currentUserNameOrEmail || "").toLowerCase();
  return body.split(/(@[a-zA-Z0-9._\-+@]{2,80})/g).map((chunk, i) => {
    if (chunk.startsWith("@")) {
      const tok = chunk.slice(1).toLowerCase();
      const isMe = me && (me.startsWith(tok) || tok.startsWith(me.split("@")[0] || me));
      return (
        <span
          key={i}
          className={
            isMe
              ? "px-1 rounded bg-amber-200 text-amber-900 font-semibold"
              : "px-1 rounded bg-blue-100 text-blue-700 font-medium"
          }
        >
          {chunk}
        </span>
      );
    }
    return <React.Fragment key={i}>{chunk}</React.Fragment>;
  });
}

const REACTION_EMOJIS = ["👍", "❤️", "✅", "🎉", "🤔", "👀", "🚀"];

function ReactionRow({ c, currentUser, onReact }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactions = c.reactions || {};
  const entries = Object.entries(reactions).filter(([, voters]) => (voters || []).length > 0);
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {entries.map(([emoji, voters]) => {
        const mine = currentUser?.id && (voters || []).includes(currentUser.id);
        return (
          <button
            key={emoji}
            onClick={() => onReact(c, emoji)}
            className={`text-xs px-1.5 py-0.5 rounded-full border ${
              mine
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {emoji} <span className="font-mono">{(voters || []).length}</span>
          </button>
        );
      })}
      <div className="relative">
        <button
          onClick={() => setPickerOpen((v) => !v)}
          className="text-slate-300 hover:text-slate-500 p-0.5"
          title="Add reaction"
        >
          <SmilePlus className="w-3.5 h-3.5" />
        </button>
        {pickerOpen && (
          <div className="absolute z-10 bottom-full mb-1 left-0 bg-white border rounded-md shadow-md p-1 flex gap-0.5">
            {REACTION_EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => { onReact(c, e); setPickerOpen(false); }}
                className="px-1.5 py-0.5 hover:bg-slate-100 rounded text-base"
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ c, mine, currentUser, onResolve, onDelete, onReact }) {
  return (
    <div className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
      <Avatar name={c.author_name} email={c.author_email} />
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-3 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
            mine
              ? "bg-blue-600 text-white rounded-tr-sm"
              : c.is_resolved
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-tl-sm"
              : "bg-slate-100 text-slate-800 rounded-tl-sm"
          }`}
        >
          {!mine && (
            <p className="text-[10px] uppercase tracking-wider font-bold mb-0.5 opacity-60">
              {c.author_name || c.author_email}
            </p>
          )}
          {highlightMentions(c.body, currentUser?.email || currentUser?.name)}
        </div>
        <ReactionRow c={c} currentUser={currentUser} onReact={onReact} />
        <div className="flex items-center gap-2 mt-0.5 px-1 text-[10px] text-slate-400">
          <span>{fmtTime(c.created_at)}</span>
          {c.edited_at && <span>· edited</span>}
          {c.is_resolved && <span className="text-emerald-600">· resolved</span>}
          {!c.is_resolved && (
            <button onClick={() => onResolve(c)} className="hover:text-emerald-600" title="Resolve">
              <Check className="w-3 h-3" />
            </button>
          )}
          {mine && (
            <button onClick={() => onDelete(c)} className="hover:text-rose-500" title="Delete">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MentionAutocomplete({ users, query, onPick }) {
  if (!query) return null;
  const q = query.toLowerCase();
  const hits = users
    .filter(
      (u) =>
        (u.full_name || u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q),
    )
    .slice(0, 6);
  if (hits.length === 0) return null;
  return (
    <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
      {hits.map((u) => (
        <button
          key={u.id}
          onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
          className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-sm"
        >
          <Avatar name={u.full_name || u.name} email={u.email} size={20} />
          <span className="truncate">
            <span className="font-medium">{u.full_name || u.name || u.email}</span>
            {u.email && (u.full_name || u.name) && (
              <span className="text-slate-400 ml-1 text-xs">{u.email}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function CommentDrawer({
  entityType,
  entityId,
  projectId,
  title,
  open,
  onOpenChange,
}) {
  const { user: currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  const refresh = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const data = await v2Api.comments.list(entityType, entityId);
      setComments(data || []);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error("Could not load comments");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    refresh();
    v2Api.users().then((u) => setUsers(u || [])).catch(() => {});
    const t = setInterval(refresh, 10000);  // poll every 10s while open
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityType, entityId]);

  const mentionQuery = useMemo(() => {
    const m = /@([a-zA-Z0-9._\-+@]{1,80})$/.exec(draft);
    return m ? m[1] : "";
  }, [draft]);

  const insertMention = (u) => {
    const handle = (u.email || u.full_name || u.name || "").split("@")[0];
    setDraft((d) => d.replace(/@([a-zA-Z0-9._\-+@]{1,80})$/, `@${handle} `));
    inputRef.current?.focus();
  };

  const send = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await v2Api.comments.create({
        entity_type: entityType,
        entity_id: entityId,
        project_id: projectId || null,
        body: draft.trim(),
      });
      setDraft("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Send failed");
    } finally { setBusy(false); }
  };

  const onResolve = async (c) => {
    try {
      await v2Api.comments.patch(c.id, { is_resolved: true });
      refresh();
    } catch { toast.error("Resolve failed"); }
  };
  const onDelete = async (c) => {
    if (!await confirm("Delete this comment?", { title: "Delete comment", confirmLabel: "Delete" })) return;
    try {
      await v2Api.comments.remove(c.id);
      refresh();
    } catch { toast.error("Delete failed"); }
  };
  const onReact = async (c, emoji) => {
    // optimistic toggle for snappy feel
    setComments((prev) => prev.map((x) => {
      if (x.id !== c.id) return x;
      const r = { ...(x.reactions || {}) };
      const list = [...(r[emoji] || [])];
      const me = currentUser?.id;
      if (me && list.includes(me)) {
        const next = list.filter((u) => u !== me);
        if (next.length) r[emoji] = next; else delete r[emoji];
      } else if (me) {
        r[emoji] = [...list, me];
      }
      return { ...x, reactions: r };
    }));
    try { await v2Api.comments.react(c.id, emoji); }
    catch { toast.error("Reaction failed"); refresh(); }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={() => onOpenChange(false)}
      />
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white border-l shadow-2xl z-50 flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              Conversation · {entityType.replace(/_/g, " ")}
            </p>
            <h3 className="font-semibold truncate flex items-center gap-2 text-slate-900">
              <MessageCircle className="w-4 h-4 text-blue-600" />
              {title || "Thread"}
            </h3>
          </div>
          <button onClick={() => onOpenChange(false)} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50/40">
          {loading && comments.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6">Loading…</p>
          )}
          {!loading && comments.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No messages yet. Start the conversation — use{" "}
              <code className="bg-slate-100 px-1 rounded">@name</code> to ping a teammate.
            </div>
          )}
          {comments.map((c) => (
            <MessageBubble
              key={c.id}
              c={c}
              mine={currentUser?.id && c.author_user_id === currentUser.id}
              currentUser={currentUser}
              onResolve={onResolve}
              onDelete={onDelete}
              onReact={onReact}
            />
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t px-3 py-2 bg-white relative">
          <MentionAutocomplete
            users={users}
            query={mentionQuery}
            onPick={insertMention}
          />
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Type a message — @ to mention. Shift+Enter for newline."
              rows={2}
              className="flex-1 resize-none rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <Button onClick={send} disabled={busy || !draft.trim()} size="sm" className="h-9">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
            <AtSign className="w-3 h-3" /> mentions notify the user.{" "}
            Resolve a thread when the question is settled.
          </p>
        </div>
      </div>
    </>
  );
}
